import { INotification, NotificationTargetObjectType } from '@models/INotification';
import { BackgroundJobKind } from '@models/backgroundJobs/IBackgroundJob';
import { RouteNames } from '@/router/router.types';

type NotificationTarget = Pick<INotification, 'targetObjectKey' | 'targetObjectType'>;

/**
 * Сервер умеет получить связанный документ только для документа, деятельности и процесса.
 * Уведомления типа Another являются системными и не должны показывать действия с документом.
 */
export const hasBoundDocumentTarget = (notification?: NotificationTarget | null): boolean => {
    if (!notification?.targetObjectKey) return false;

    return (
        notification.targetObjectType === NotificationTargetObjectType.Document ||
        notification.targetObjectType === NotificationTargetObjectType.Activity ||
        notification.targetObjectType === NotificationTargetObjectType.Process
    );
};

/** Якорь раздела «Доступ поддержки» на странице «Мои настройки». */
export const SUPPORT_ACCESS_SECTION_ID = 'support-access';
export const SUPPORT_ACCESS_PATH = `${RouteNames.SETTINGS}#${SUPPORT_ACCESS_SECTION_ID}`;

/** Уведомление о доступе поддержки (`support.access`) ведёт в раздел «Доступ поддержки», а не к документу. */
export const hasSupportAccessTarget = (notification?: Pick<INotification, 'tag'> | null): boolean => {
    const tag = notification?.tag?.toLowerCase();
    return tag === 'support.access' || !!tag?.startsWith('support.access.');
};

/**
 * Вид фоновой задачи, об итоге которой уведомление: `job.{вид}.{итог}` или `report.{итог}` (запуск отчёта, вид
 * `report`); не итог задачи — `null`.
 */
export const backgroundJobNotificationKind = (
    notification?: Pick<INotification, 'tag'> | null,
): BackgroundJobKind | null => {
    const tag = notification?.tag;
    const lower = tag?.toLowerCase();
    if (!tag || !lower) return null;
    if (lower.startsWith('report.')) return 'report';
    if (lower.startsWith('job.')) return tag.split('.')[1] ?? '';
    return null;
};

/**
 * Итог фоновой задачи (`job.*`, `report.*`; цель — сама задача, `TargetObjectType=Another`): идентификатор задачи для
 * её действий (файл результата, документ, страница отчёта); иначе `null`.
 */
export const backgroundJobTargetId = (
    notification?: Pick<INotification, 'tag' | 'targetObjectKey' | 'targetObjectType'> | null,
): string | null => {
    if (backgroundJobNotificationKind(notification) === null || !notification?.targetObjectKey) return null;
    if (
        notification.targetObjectType != null &&
        notification.targetObjectType !== NotificationTargetObjectType.Another
    ) {
        return null;
    }
    return notification.targetObjectKey;
};
